import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, CalendarCheck } from "lucide-react";
import { SITE, NAV_LINKS } from "@/config/site";
import { telLink } from "@/utils/whatsapp";
import { Logo } from "@/components/Logo";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const linkCls = ({ isActive }) =>
    `rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-600 ${
      isActive ? "bg-navy-50 text-navy-800" : "text-slate-600 hover:bg-slate-100 hover:text-navy-800"
    }`;

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Top info bar */}
      <div className="hidden bg-navy-900 text-white md:block" data-testid="header-topbar">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-xs sm:px-6 lg:px-8">
          <p className="text-slate-300">{SITE.location}</p>
          <a href={telLink} className="flex items-center gap-1.5 font-medium text-white hover:text-emerald-300" data-testid="topbar-call-link">
            <Phone className="h-3.5 w-3.5" aria-hidden="true" /> {SITE.phoneDisplay}
          </a>
        </div>
      </div>

      {/* Main bar */}
      <div className={`border-b border-slate-200/80 bg-white/90 backdrop-blur-md transition-shadow duration-300 ${scrolled ? "shadow-md" : "shadow-sm"}`}>
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:h-[72px] sm:px-6 lg:px-8">
          <Link to="/" aria-label={`${SITE.name} — home`} data-testid="header-logo-link">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.path} to={l.path} end={l.path === "/"} className={linkCls} data-testid={`nav-link-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.name}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={telLink}
              className="hidden items-center gap-2 rounded-full border border-navy-200 px-4 py-2 text-sm font-semibold text-navy-800 transition-colors hover:bg-navy-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-600 md:flex"
              data-testid="header-call-button"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phoneDisplay}
            </a>
            <Link
              to="/appointments"
              className="hidden items-center gap-2 rounded-full bg-navy-800 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-navy-700 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-600 sm:flex"
              data-testid="header-book-appointment-button"
            >
              <CalendarCheck className="h-4 w-4" aria-hidden="true" /> Book Appointment
            </Link>
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 text-navy-900 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-600 lg:hidden"
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label="Menu" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-navy-950/50 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="mobile-nav-backdrop" />
          <div className="absolute right-0 top-0 flex h-full w-80 max-w-[85vw] flex-col bg-white shadow-2xl animate-drawer-in">
            <div className="flex items-center justify-between bg-navy-900 px-5 py-4">
              <Logo light />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                data-testid="mobile-menu-close-button"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-4 py-4" aria-label="Mobile">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  end={l.path === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `mb-1 block rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                      isActive ? "bg-navy-50 text-navy-800" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                  data-testid={`mobile-nav-link-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.name}
                </NavLink>
              ))}
            </nav>
            <div className="space-y-2 border-t border-slate-100 p-4">
              <Link
                to="/appointments"
                onClick={() => setOpen(false)}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-navy-800 px-4 py-3 text-sm font-semibold text-white"
                data-testid="mobile-book-appointment-button"
              >
                <CalendarCheck className="h-4 w-4" aria-hidden="true" /> Book Appointment
              </Link>
              <a
                href={telLink}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-navy-200 px-4 py-3 text-sm font-semibold text-navy-800"
                data-testid="mobile-call-button"
              >
                <Phone className="h-4 w-4" aria-hidden="true" /> Call {SITE.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
